/**
 * Примитивы работы с канвой: декодирование файла, отрисовка, кодирование обратно
 * в блоб, проба альфа-канала и переименование под новый тип.
 *
 * Вынесены из `compress-image.ts`, потому что кадрированию (`crop-image.ts`) нужны
 * ровно те же четыре шага — и, главное, те же решения: EXIF-поворот применяется на
 * входе (канва его теряет), а прозрачность определяет выходной формат. Дублировать
 * их значило бы получить два разных ответа на один вопрос.
 */

export type CompressImageFailureReason =
  /** Браузер не смог декодировать файл — битый файл или формат вроде HEIC. */
  | 'decode-failed'
  /** Канва не отдала данные. */
  | 'encode-failed'
  /** Даже на последней ступени лестницы результат не уложился в бюджет. */
  | 'too-large';

/**
 * Общая ошибка всех операций над изображением.
 *
 * Живёт здесь, а не в `compress-image.ts`, чтобы примитивы канвы не зависели от
 * сжатия: иначе `crop-image.ts` тянул бы весь алгоритм сжатия ради одного класса.
 * Имя оставлено прежним — это публичный контракт, на него смотрят потребители
 * (`use-image-compression.ts`) и тесты; `compress-image.ts` его реэкспортирует.
 */
export class CompressImageError extends Error {
  constructor(readonly reason: CompressImageFailureReason, message?: string) {
    super(message ?? reason);
    this.name = 'CompressImageError';
  }
}

export interface ImageSize {
  width: number;
  height: number;
}

export const EXTENSION_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/png': 'png',
};

/** Форматы, у которых в принципе может быть альфа-канал. У JPEG её нет никогда. */
export const TYPES_WITH_POSSIBLE_ALPHA = ['image/png', 'image/webp', 'image/gif'];

/** Сторона, на которой проверяется прозрачность: полноразмерный ImageData не нужен. */
export const ALPHA_PROBE_MAX_SIDE = 512;

export interface DecodedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}

/**
 * Декодирование с учётом EXIF-поворота.
 *
 * Канва теряет EXIF, поэтому ориентацию нужно применить на входе — иначе снятые
 * вертикально фото с iPhone лягут набок. `imageOrientation` поддерживают Chrome 79+,
 * Firefox 77+ и Safari 15+; там, где опции или самого `createImageBitmap` нет,
 * остаётся `<img>` без коррекции поворота.
 */
export async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
    } catch {
      try {
        const bitmap = await createImageBitmap(file);
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close?.() };
      } catch {
        // Падаем в вариант с <img>: он умеет меньше, но зато есть везде.
      }
    }
  }

  return decodeWithImageElement(file);
}

export function decodeWithImageElement(file: File): Promise<DecodedImage> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        close: () => URL.revokeObjectURL(url),
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new CompressImageError('decode-failed', `Не удалось декодировать ${file.type || 'файл'}`));
    };

    image.src = url;
  });
}

export function scaledSize(width: number, height: number, maxSide?: number): ImageSize {
  if (!maxSide) return { width, height };

  const longest = Math.max(width, height);
  if (longest <= maxSide) return { width, height };

  const ratio = maxSide / longest;
  return { width: Math.round(width * ratio), height: Math.round(height * ratio) };
}

export function drawToCanvas(image: DecodedImage, size: ImageSize): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext('2d');
  if (!context) throw new CompressImageError('encode-failed', 'Канва недоступна');

  context.drawImage(image.source, 0, 0, size.width, size.height);
  return canvas;
}

export function toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new CompressImageError('encode-failed', 'Канва не отдала данные'))),
      type,
      quality,
    );
  });
}

/**
 * Есть ли в изображении хоть один непрозрачный пиксель.
 *
 * Проверка идёт по уменьшенной копии: полноразмерный `ImageData` восьмимегапиксельного
 * фото — это две сотни мегабайт. При уменьшении прозрачная область остаётся
 * прозрачной, поэтому вырезанный фон определяется надёжно.
 */
export function hasAlphaChannel(image: DecodedImage, sourceType: string): boolean {
  if (!TYPES_WITH_POSSIBLE_ALPHA.includes(sourceType)) return false;

  const probeSize = scaledSize(image.width, image.height, ALPHA_PROBE_MAX_SIDE);
  const canvas = drawToCanvas(image, probeSize);
  const context = canvas.getContext('2d');
  if (!context) return false;

  const { data } = context.getImageData(0, 0, probeSize.width, probeSize.height);
  for (let index = 3; index < data.length; index += 4) {
    if (data[index] < 255) return true;
  }

  return false;
}

export function renameForType(fileName: string, type: string): string {
  const extension = EXTENSION_BY_TYPE[type] ?? 'jpg';
  const base = fileName.replace(/\.[^./\\]+$/, '') || 'photo';
  return `${base}.${extension}`;
}
